import { Module } from 'vuex';

import { CourseEditInfo, RootState, Nullable, GlobalState } from 'frontend/types';
import {
  CourseAddChapterInput,
  CourseAddLessonInput,
  CourseCreateApiInput,
  CourseCreateLessonInput,
  CourseChangeStateInput,
  CourseRemoveChapterInput,
  CourseRemoveLessonInput,
  CourseUpdateChapterInput,
  CourseUpdateChapterOrderInput,
  CourseUpdateContentInput,
  CourseUpdateLessonOrderInput,
} from 'frontend/types/authoring/requests';
import { apiResolve } from 'frontend/router';
import { apiRequestWithErrors } from 'frontend/store/modules/lessons/apiHelpers';
import { deepCopyObj } from 'frontend/utils/copy';
import { debounce } from 'frontend/utils/timing';
import { slugify, uniqueString } from 'frontend/utils/formatting';
import { LessonCreateApiInputs, LessonCreateApiResult } from 'frontend/types/lessons/requests';

interface CourseEditState {
  course?: Nullable<CourseEditInfo>;
  requestErrors: string[];
  waitingOnRequest: boolean;
}

/**
 * When moving lessons between chapters, the update function is called twice. However, we only
 * want to make one request. Therefore, use this to wrap the request so it only happens once.
 */
const courseContentUpdater = debounce((f: () => void) => f(), 50);

/** Simplify created typechecked CourseUpdateContentInput object */
function courseUpdateInput(updatedCourse: CourseEditInfo): CourseUpdateContentInput {
  return { course: updatedCourse };
}

interface ExtendedRootState extends RootState {
  globals: GlobalState;
}

const courseEdit: Module<CourseEditState, ExtendedRootState> = {
  namespaced: true,
  state: {
    course: null,
    requestErrors: [],
    waitingOnRequest: false,
  },
  mutations: {
    setValue(state, value: CourseEditInfo): void {
      state.course = value;
    },
    setCourseResponse(state, result: CourseEditInfo | null): void {
      state.course = result;
    },
    setErrors(state, errors: string[]): void {
      state.requestErrors = errors;
    },
    setWaiting(state, waitingOnRequest: boolean): void {
      state.waitingOnRequest = waitingOnRequest;
    },
  },
  actions: {
    resetErrors({ commit }) {
      commit('setErrors', []);
    },
    async createCourse({ commit }, { params, formData }: CourseCreateApiInput): Promise<void> {
      commit('setWaiting', true);

      const { href } = apiResolve({ name: 'course_create' });
      const [success, result, errors] = await apiRequestWithErrors(href, formData);

      commit('setErrors', errors);
      if (success) {
        commit('setCourseResponse', result?.course);
      }
      commit('setWaiting', false);

      if (!success) {
        throw new Error('Course creation request not successful.');
      }
    },
    async createCourseVersion({ commit }, { formData }: CourseCreateApiInput): Promise<void> {
      commit('setWaiting', true);

      const { href } = apiResolve({ name: 'course_create_version' });
      const [success, result, errors] = await apiRequestWithErrors(href, formData);

      commit('setErrors', errors);
      if (success) {
        commit('setCourseResponse', result?.course);
      }
      commit('setWaiting', false);

      if (!success) {
        throw new Error('Course version creation request not successful.');
      }
    },
    async changeState({ commit }, { params, formData }: CourseChangeStateInput): Promise<void> {
      commit('setWaiting', true);

      const { href } = apiResolve({ params, name: 'course_change_state' });
      const [success, result, errors] = await apiRequestWithErrors(href, formData);

      commit('setErrors', errors);
      if (success) {
        commit('setCourseResponse', result?.course_edit);
      }
      commit('setWaiting', false);

      if (!success) {
        throw new Error('Course state change request not successful.');
      }
    },
    updateCourseContent({ commit }, { course }: CourseUpdateContentInput) {
      // Set these outside of the updater function so that the UI is updated instantly, both for
      // the something-is-happening indicator and for the course outline
      commit('setWaiting', true);
      // Update the value immediately
      commit('setValue', course);
      // Do this so the request only happens once for multiple updates
      courseContentUpdater(async () => {
        const { href } = apiResolve({ name: 'course_update_content' });
        const [_, result, errors] = await apiRequestWithErrors(href, { course });
        if (result?.course_edit) {
          // Update the value again. If we've created a new chapter/lesson, it'll have an id now
          commit('setValue', result?.course_edit);
        }
        commit('setErrors', errors);
        commit('setWaiting', false);
      });
    },

    addChapter({ dispatch, state }, { chapter }: CourseAddChapterInput) {
      if (!state.course) {
        return;
      }
      const updatedCourse: CourseEditInfo = deepCopyObj(state.course);
      const newChapter = {
        ...chapter,
        slug: uniqueString(
          chapter.slug,
          state.course.chapters.map(c => c.slug),
        ),
      };
      updatedCourse.chapters = [...updatedCourse.chapters, newChapter];
      dispatch('updateCourseContent', courseUpdateInput(updatedCourse));
    },

    updateChapter({ dispatch, state }, { chapterSlug, updatedChapter }: CourseUpdateChapterInput) {
      if (!state.course) {
        return;
      }
      const updatedCourse: CourseEditInfo = deepCopyObj(state.course);
      updatedCourse.chapters = updatedCourse.chapters.map(chapter => {
        if (chapter.slug === chapterSlug) {
          return updatedChapter;
        }
        return chapter;
      });
      dispatch('updateCourseContent', courseUpdateInput(updatedCourse));
    },

    updateLessonOrder({ dispatch }, { lessons, chapter, course }: CourseUpdateLessonOrderInput) {
      const updatedCourse: CourseEditInfo = deepCopyObj(course);

      // Overwrite lessons for the appropriate chapter
      updatedCourse.chapters = updatedCourse.chapters.map(courseChapter => {
        if (courseChapter.slug === chapter.slug) {
          return { ...courseChapter, lessons };
        }
        return courseChapter;
      });

      dispatch('updateCourseContent', courseUpdateInput(updatedCourse));
    },

    removeLesson({ dispatch, state }, { chapterSlug, removedLessonSlug }: CourseRemoveLessonInput) {
      if (!state.course) {
        return;
      }
      // Filter out the removed lesson for the appropriate chapter
      const updatedCourse: CourseEditInfo = deepCopyObj(state.course);
      updatedCourse.chapters = updatedCourse.chapters.map(courseChapter => {
        if (courseChapter.slug === chapterSlug) {
          return {
            ...courseChapter,
            lessons: courseChapter.lessons.filter(l => l.slug !== removedLessonSlug),
          };
        }
        return courseChapter;
      });

      dispatch('updateCourseContent', courseUpdateInput(updatedCourse));
    },

    addLesson({ dispatch, state }, { chapterSlug, newLesson }: CourseAddLessonInput) {
      if (!state.course) {
        return;
      }

      const updatedCourse: CourseEditInfo = deepCopyObj(state.course);
      updatedCourse.chapters = updatedCourse.chapters.map(chapter => {
        if (chapter.slug === chapterSlug) {
          return { ...chapter, lessons: [...chapter.lessons, newLesson] };
        }
        return chapter;
      });

      dispatch('updateCourseContent', courseUpdateInput(updatedCourse));
    },

    async createLesson(
      { commit, dispatch, state, rootState },
      { chapterSlug, lessonTitle, addExampleContent }: CourseCreateLessonInput,
    ): Promise<boolean> {
      if (!state.course || !rootState.globals.user) {
        return false;
      }
      commit('setWaiting', true);
      const lessonSlug = slugify(lessonTitle);
      const inputs: LessonCreateApiInputs = {
        params: {
          chapterSlug,
          lessonSlug,
          courseSlug: state.course.slug,
        },
        formData: {
          title: lessonTitle,
          description: '',
          version_name: 'V0',
          lesson_slug: lessonSlug,
          author_id: rootState.globals.user.user_id.toString(),
          add_example_content: addExampleContent,
          for_v3_course: true,
        },
      };
      const { data, errors }: LessonCreateApiResult = await dispatch(
        'lessonEditor/createLesson',
        inputs,
        { root: true },
      );

      if (data?.lesson) {
        const addLessonInput: CourseAddLessonInput = {
          chapterSlug,
          newLesson: {
            lesson_id: data.lesson.lesson_id,
            slug: data.lesson_slug,
            lesson_version_id: data.lesson.version_id,
            version_name: data.lesson.version_name,
            state: data.lesson.state,
            title: data.lesson.title,
            description: data.lesson.description,
            image:
              data.lesson.image_url && data.lesson.image_url.length > 0
                ? data.lesson.image_url
                : undefined,
          },
        };
        dispatch('addLesson', addLessonInput);
        return true;
      }

      commit('setErrors', errors);
      commit('setWaiting', false);
      return false;
    },

    reorderChapters({ dispatch, state }, { chapters }: CourseUpdateChapterOrderInput) {
      if (!state.course) {
        return;
      }
      const updatedCourse: CourseEditInfo = { ...deepCopyObj(state.course), chapters };
      dispatch('updateCourseContent', courseUpdateInput(updatedCourse));
    },

    removeChapter({ dispatch, state }, { chapterSlug }: CourseRemoveChapterInput) {
      if (!state.course) {
        return;
      }
      const updatedCourse: CourseEditInfo = deepCopyObj(state.course);
      updatedCourse.chapters = updatedCourse.chapters.filter(
        chapter => chapter.slug !== chapterSlug,
      );
      dispatch('updateCourseContent', courseUpdateInput(updatedCourse));
    },
  },

  getters: {
    courseEditRequestErrors(state: CourseEditState): string[] {
      return state.requestErrors;
    },
    courseEditWaitingOnRequest(state: CourseEditState): boolean {
      return state.waitingOnRequest;
    },
  },
};

export default courseEdit;
